import { client } from '@/client';
import { ImpersonationTokenResponse } from '@/client/auth';
import {
  AppRoutesPaths,
  LAST_SELECTED_ACCOUNT_SETTINGS,
} from '@/common/constants';
import { IMPERSONATION_TOKEN_KEY } from '@/common/constants/user';
import { useToast } from '@/hooks/useToast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useImpersonation = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationToken, setImpersonationToken] = useState<string | null>(
    null,
  );

  // Helper function to check if we're currently impersonating
  const isCurrentlyImpersonating = (): boolean => {
    const sessionToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
    return !!sessionToken;
  };

  // Helper function to get the current impersonation token
  const getCurrentImpersonationToken = (): string | null => {
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
  };

  const startImpersonation = async (targetUserId: string): Promise<void> => {
    try {
      setIsImpersonating(true);
      const response: ImpersonationTokenResponse =
        await client.impersonation.startImpersonation(targetUserId);

      // Store the token for potential future use
      setImpersonationToken(response.accessToken);

      // Store token in sessionStorage for persistence across navigation
      sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, response.accessToken);

      // Show success message
      toast?.success(
        t('impersonation.success.title'),
        t('impersonation.success.message', {
          user: response.impersonatedUser.username,
        }),
      );

      sessionStorage.setItem(LAST_SELECTED_ACCOUNT_SETTINGS, 'false');

      const deployPath = process.env.REACT_APP_PREVIEW_DEPLOY_PATH;

      // Redirect to home without token in URL
      window.location.href = new URL(
        deployPath ? `/${deployPath}` : AppRoutesPaths.BASE,
        window.location.origin,
      ).toString();
    } catch (error) {
      setIsImpersonating(false);
      const apiDetail = (error as { errors?: string })?.errors;
      toast?.error(
        t('impersonation.error.title'),
        typeof apiDetail === 'string'
          ? apiDetail
          : t('impersonation.error.message'),
      );
    }
  };

  const endImpersonation = (): void => {
    setIsImpersonating(false);
    setImpersonationToken(null);

    // Clear token from sessionStorage
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);

    toast?.success(
      t('impersonation.end.title'),
      t('impersonation.end.message'),
    );

    // Reload to current page without any URL modifications
    const deployPath = process.env.REACT_APP_PREVIEW_DEPLOY_PATH;
    window.location.href = new URL(
      deployPath
        ? `/${deployPath}${AppRoutesPaths.ADMIN_DASHBOARD_ROUTE}`
        : AppRoutesPaths.ADMIN_DASHBOARD_ROUTE,
      window.location.origin,
    ).toString();
  };

  return {
    isImpersonating,
    impersonationToken,
    startImpersonation,
    endImpersonation,
    isCurrentlyImpersonating,
    getCurrentImpersonationToken,
  };
};
